import {
  ADMIN_TRAFFIC_SOURCES,
  type AdminTrafficSource,
  type LandingVisitAttribution,
} from "../../domain/operations";

const VISIT_STORAGE_PREFIX = "banryeojusik.landing-visit.v1";
const SESSION_ATTRIBUTION_KEY = "banryeojusik.landing-attribution.v1";

interface LandingVisitOptions {
  readonly fetcher?: typeof fetch;
  readonly now?: Date;
  readonly referrer?: string;
  readonly storage?: Storage;
  readonly url?: string;
}

export async function recordLandingVisitOnce(
  options: LandingVisitOptions = {},
): Promise<"recorded" | "skipped" | "failed"> {
  const storage = options.storage ?? window.sessionStorage;
  const now = options.now ?? new Date();
  const key = `${VISIT_STORAGE_PREFIX}:${localDate(now)}`;
  const attribution = buildLandingVisitAttribution(
    options.url ?? browserLocation(),
    options.referrer ?? browserReferrer(),
  );
  try {
    rememberSessionAttribution(storage, attribution);
    if (storage.getItem(key) === "1") {
      return "skipped";
    }
    storage.setItem(key, "1");
  } catch {
    return "failed";
  }

  try {
    const response = await (options.fetcher ?? fetch)("/api/traffic/visit", {
      body: JSON.stringify(attribution),
      cache: "no-store",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      method: "POST",
      referrerPolicy: "no-referrer",
    });
    return response.status === 204 ? "recorded" : "failed";
  } catch {
    return "failed";
  }
}

export function readSessionLandingAttribution(
  storage: Storage,
): LandingVisitAttribution | null {
  try {
    const raw = storage.getItem(SESSION_ATTRIBUTION_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    return isLandingVisitAttribution(value) ? value : null;
  } catch {
    return null;
  }
}

export function buildLandingVisitAttribution(
  currentUrl: string,
  referrer: string,
): LandingVisitAttribution {
  const current = safeUrl(currentUrl) ?? new URL("https://invalid.local/");
  const referring = safeUrl(referrer);
  const utmSource = campaignValue(current.searchParams.get("utm_source"));
  const utmMedium = campaignValue(current.searchParams.get("utm_medium"));
  const campaign = campaignValue(current.searchParams.get("utm_campaign"));
  const referrerHost = referring ? normalizeHost(referring.hostname) : null;
  const source = classifySource({
    currentHost: normalizeHost(current.hostname),
    referrerHost,
    utmSource,
  });
  return {
    campaign,
    entryPath: safeEntryPath(current.pathname),
    referrerHost,
    schemaVersion: 1,
    source,
    utmMedium,
    utmSource,
  };
}

export function attributedDownloadUrl(
  downloadUrl: string,
  currentUrl: string,
): string {
  const target = safeUrl(downloadUrl);
  const current = safeUrl(currentUrl);
  if (!target || !current) return downloadUrl;
  for (const key of ["utm_source", "utm_medium", "utm_campaign"] as const) {
    const value = campaignValue(current.searchParams.get(key));
    if (value) target.searchParams.set(key, value);
  }
  return target.toString();
}

function classifySource(input: {
  readonly currentHost: string;
  readonly referrerHost: string | null;
  readonly utmSource: string | null;
}): AdminTrafficSource {
  const campaignSource = input.utmSource?.toLowerCase() ?? "";
  if (campaignSource.includes("thread")) return "threads";
  if (campaignSource.includes("instagram")) return "instagram";
  if (campaignSource === "direct") return "direct";
  if (
    campaignSource.includes("search") ||
    ["google", "bing", "naver", "daum", "duckduckgo"]
      .includes(campaignSource)
  ) return "search";
  if (
    campaignSource.includes("social") ||
    ["facebook", "linkedin", "reddit", "t.co", "twitter", "x", "youtube"]
      .includes(campaignSource)
  ) return "social";
  if (campaignSource) return "other";
  const host = input.referrerHost;
  if (!host) return "direct";
  if (host === input.currentHost) return "internal";
  if (host === "threads.net" || host.endsWith(".threads.net")) {
    return "threads";
  }
  if (host === "instagram.com" || host.endsWith(".instagram.com")) {
    return "instagram";
  }
  if (
    ["google.com", "bing.com", "naver.com", "daum.net", "duckduckgo.com"]
      .some((domain) => host === domain || host.endsWith(`.${domain}`))
  ) {
    return "search";
  }
  if (
    ["facebook.com", "linkedin.com", "t.co", "twitter.com", "x.com", "youtube.com"]
      .some((domain) => host === domain || host.endsWith(`.${domain}`))
  ) {
    return "social";
  }
  return "other";
}

function safeUrl(value: string): URL | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function normalizeHost(value: string): string {
  return value.toLowerCase().replace(/^www\./u, "").slice(0, 128);
}

function campaignValue(value: string | null): string | null {
  if (!value || value.length > 64 || !/^[a-z0-9._~-]+$/iu.test(value)) {
    return null;
  }
  return value;
}

function rememberSessionAttribution(
  storage: Storage,
  attribution: LandingVisitAttribution,
): void {
  if (readSessionLandingAttribution(storage)) return;
  storage.setItem(SESSION_ATTRIBUTION_KEY, JSON.stringify(attribution));
}

function isLandingVisitAttribution(
  value: unknown,
): value is LandingVisitAttribution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return record.schemaVersion === 1 &&
    typeof record.source === "string" &&
    ADMIN_TRAFFIC_SOURCES.includes(record.source as AdminTrafficSource) &&
    typeof record.entryPath === "string" &&
    record.entryPath.startsWith("/") &&
    record.entryPath.length <= 128 &&
    !/[?#]/u.test(record.entryPath) &&
    isStoredHost(record.referrerHost) &&
    isStoredCampaign(record.utmSource) &&
    isStoredCampaign(record.utmMedium) &&
    isStoredCampaign(record.campaign);
}

function isStoredHost(value: unknown): value is string | null {
  return value === null || (
    typeof value === "string" && value.length >= 1 && value.length <= 128 &&
    /^[a-z0-9.-]+$/u.test(value)
  );
}

function isStoredCampaign(value: unknown): value is string | null {
  return value === null || (
    typeof value === "string" && value.length >= 1 && value.length <= 64 &&
    /^[a-z0-9._~-]+$/iu.test(value)
  );
}

function safeEntryPath(value: string): string {
  return value.startsWith("/") ? value.slice(0, 128) || "/" : "/";
}

function localDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function browserLocation(): string {
  return typeof window === "undefined"
    ? "https://invalid.local/"
    : window.location.href;
}

function browserReferrer(): string {
  return typeof document === "undefined" ? "" : document.referrer;
}
